/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/

import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { DataGrid } from "@mui/x-data-grid";
import {
  Box,
  Typography,
  TextField,
  Toolbar,
  InputAdornment,
  Alert,
  Snackbar,
  Popover,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Backdrop,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Collapse
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import Topheader from "../../../Layout/header/header";
import {
  Search,
  KeyboardArrowDown,
  KeyboardArrowUp,
  ArrowDropUpRounded,
} from "@mui/icons-material";
import "./global-admin-applicationList.css";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import APIManager from "../../../Services/ApiManager/ApiManager";
import { fetchDeletedApplications } from "../../../Services/redux/DeletedApplications/DeletedApplicationsActions";
import { DrawerFilter } from "../../../Components/ApplicationStatistics/Developer/DrawerFilter";
import { 
  fetchDomainApproverCountriesDetails, 
  forceFreshCountriesDetails 
} from "../../../Services/redux/domain-approver-countries/DomainApproverCountryActions";
import { 
  fetchDomainApproverEcuDetails,
  forceFreshEcuDetails
} from "../../../Services/redux/domain-approver-ecu-types/DomainApproverEcuActions";
import { fetchCognitoUsers } from "../../../Services/redux/cognito-users/CognitoUsersActions";
import PropTypes from "prop-types";

function NoRowsOverlay({ showNoAppImage }) {
  return (
    <Box
      height="100%"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <Typography variant="body2" color="textSecondary">
        {showNoAppImage ? "No applications found." : "Loading..."}
      </Typography>
    </Box>
  );
}

NoRowsOverlay.propTypes = {
  showNoAppImage: PropTypes.bool,
};

export default function GlobalAdminApplicationList() {
  const [publisherIdName, setPublisherIdName] = useState([]);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const readOnly_GlobalAdmin =
    sessionStorage.getItem("readonly_GlobalAdmin") === "true";
  // Redux state
  const { domainApproverContents } =
    useSelector((state) => state.contentAppList);

  // Deleted applications Redux state
  const { deletedApplications, loading: deletedApplicationsLoading } =
    useSelector((state) => state.deletedApplications);

  // Filter data Redux state
  const countries = useSelector(
    (state) => state.domainApproverCountries.countries
  );
  const ecuTypeData = useSelector((state) => state.domainApproverEcuTypes.ecu);

  // Memoized filter data with safety checks
  const memoizedCountries = useMemo(() => {
    return Array.isArray(countries) ? countries : [];
  }, [countries]);

  const createEcuTypeEntry = (ecuType, make) => ({
    name: ecuType,
    ecuType: ecuType,
    make: make,
    toString: () => ecuType,
  });

  const collectEcuTypes = (flatEcuTypes, makeData) => {
    if (makeData.ecuTypes && Array.isArray(makeData.ecuTypes)) {
      makeData.ecuTypes.forEach((ecuType) => {
        if (typeof ecuType === "string") {
          flatEcuTypes.push(createEcuTypeEntry(ecuType, makeData.make));
        }
      });
    }
  };

  const memoizedEcu = useMemo(() => {
    if (!Array.isArray(ecuTypeData)) {
      return [];
    }

    const flatEcuTypes = [];
    ecuTypeData.forEach((makeData) => collectEcuTypes(flatEcuTypes, makeData));

    // Remove duplicates based on ecuType name to avoid redundancy
    const seenEcuTypes = new Set();
    return flatEcuTypes.filter((ecu) => {
      if (seenEcuTypes.has(ecu.ecuType)) return false;
      seenEcuTypes.add(ecu.ecuType);
      return true;
    });
  }, [ecuTypeData]);

  // Local state
  const [searchTerm, setSearchTerm] = useState("");
  const selectedTab = 0; // Default to Group Apps tab
  const [activeStatus, setActiveStatus] = useState(() => {
    return (
      sessionStorage.getItem("globalAdminActiveStatus") ||
      "SUBMITTED_FOR_PRODUCTION"
    );
  });
  const [activeThirdPartyStatus, setActiveThirdPartyStatus] = useState(
    "REJECTED_BY_DOMAIN_APPROVER"
  );
  const [thirdPartyApplications, setThirdPartyApplications] = useState([]);
  const [openSections, setOpenSections] = useState({
    Beta: false,
    Staging: false,
    Production: true,
  });

  // Check idpLogin flag from session storage
  const idpLogin = sessionStorage.getItem("idpLogin") === "true";

  const [applicationList, setApplicationList] = useState([]);
  const [allApplicationsData, setAllApplicationsData] = useState({}); // Store all fetched applications by status
  const [loader, setLoader] = useState(true);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [showNoAppImage, setShowNoAppImage] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [openErrorPopup, setOpenErrorPopup] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [openSuccessPopup, setOpenSuccessPopup] = useState(false);

  // Ref to track if initial data has been loaded
  const hasLoadedInitialData = useRef(false);
  const loaderStartTime = useRef(null);
  const filterDataRefreshIntervalRef = useRef(null); // Ref for 5-minute refresh interval

  // Sorting state
  const [sortBy, setSortBy] = useState("lastModified"); // Default sort by Last Modified
  const [sortOrder, setSortOrder] = useState("asc"); // "asc" or "desc"
  const [hoveredColumn, setHoveredColumn] = useState(null); // Track which column header is hovered

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Reset page to 0 when search term, active status, or sort order changes
  useEffect(() => {
    setPage(0);
  }, [
    searchTerm,
    activeStatus,
    activeThirdPartyStatus,
    selectedTab,
    sortBy,
    sortOrder,
  ]);

  // Helper function to reset pagination
  const resetPagination = () => {
    setPage(0);
  };

  // Handle sort column change
  const handleSort = (column) => {
    if (sortBy === column) {
      // Same column - toggle sort order
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      // Different column - set new column and default to ascending
      setSortBy(column);
      setSortOrder("asc");
    }
  };

  const [ecuTypesPopup, setEcuTypesPopup] = useState({
    open: false,
    data: [],
    anchorEl: null,
  });
  const [countriesPopup, setCountriesPopup] = useState({
    open: false,
    data: [],
    anchorEl: null,
  });

  const clickApplications = (clickedRow) => {
    sessionStorage.setItem("globalAdminNavigatingToDetails", "true");

    if (
      clickedRow.row?.currentSubmission &&
      Object.keys(clickedRow.row.currentSubmission).length > 0
    ) {
      const applicationId = clickedRow.id.includes("group_")
        ? clickedRow.id.replaceAll("group_", "")
        : clickedRow.id.replaceAll(
            `_submission_${clickedRow.row.currentSubmission.id}`,
            ""
          );
      navigate(
        `/ui/harman-admin/application/${applicationId}/submission/${clickedRow.row.currentSubmission.id}`
      );
    } else {
      navigate(
        `/ui/harman-admin/application/${clickedRow.id}/third-party-apps`
      );
    }
  };

  // Confirmation dialog state for delete action
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({
    open: false,
    applicationId: null,
    applicationName: "",
  });

  // Confirmation dialog state for reject action
  const [rejectConfirmDialog, setRejectConfirmDialog] = useState({
    open: false,
    applicationId: null,
    applicationName: "",
  });

  // Loading state for reject operation
  const [rejectLoading, setRejectLoading] = useState(false);

  // State for tracking expanded application groups
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  // State for filter drawer
  const [openFilter, setOpenFilter] = useState(false);

  // Create a ref to store the loading state to avoid including it in dependencies
  const isCountsLoadingRef = useRef(false);

  const handleEcuTypesClose = () => {
    setEcuTypesPopup({
      open: false,
      data: [],
      anchorEl: null,
    });
  };

  const handleCountriesClose = () => {
    setCountriesPopup({
      open: false,
      data: [],
      anchorEl: null,
    });
  };

  // Handle delete button click
  const handleDeleteClick = (applicationId, applicationName) => {
    setDeleteConfirmDialog({
      open: true,
      applicationId,
      applicationName,
    });
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    const { applicationId } = deleteConfirmDialog;
    try {
      await APIManager.deleteDeletedApplication(applicationId);

      // Refresh the deleted applications list
      dispatch(fetchDeletedApplications());

      // Refresh counts
      loadAllGroupAppsCounts();

      // Show success message
      setSuccessMessage("Application deleted successfully");
      setOpenSuccessPopup(true);
    } catch (error) {
      setErrorMessage(error?.message || "Failed to delete application");
      setOpenErrorPopup(true);
    } finally {
      setDeleteConfirmDialog({
        open: false,
        applicationId: null,
        applicationName: "",
      });
    }
  };

  // Handle delete cancel
  const handleDeleteCancel = () => {
    setDeleteConfirmDialog({
      open: false,
      applicationId: null,
      applicationName: "",
    });
  };

  // Handle reject button click
  const handleRejectClick = (applicationId, applicationName) => {
    setRejectConfirmDialog({
      open: true,
      applicationId,
      applicationName,
    });
  };

  // Handle reject confirmation
  const handleRejectConfirm = async () => {
    const { applicationId } = rejectConfirmDialog;
    setRejectLoading(true);

    try {
      await APIManager.rejectDeletedApplication(applicationId);

      // Refresh the deleted applications list
      dispatch(fetchDeletedApplications());
      try {
        const submittedApps = await APIManager.getAllApplication(
          "SUBMITTED_FOR_PRODUCTION"
        );
        if (
          submittedApps &&
          submittedApps.length > 0 &&
          !submittedApps[0].message
        ) {
          const groupedApplications = normalizeAndGroupApplications(submittedApps);
          const processedApps = groupedApplications.map((app, index) => {
            const submissionsArray = Array.isArray(app.submissions) ? app.submissions : [];
            return {
              ...app,
              id: app.id || app.applicationId || index + 1,
              currentSubmission: findRelevantSubmission(submissionsArray, "SUBMITTED_FOR_PRODUCTION"),
            };
          });
          setAllApplicationsData((prev) => ({
            ...prev,
            SUBMITTED_FOR_PRODUCTION: processedApps,
          }));
          if (activeStatus === "SUBMITTED_FOR_PRODUCTION") {
            setApplicationList(processedApps);
          }
        }
      } catch (err) {
        console.error("Error refreshing submitted applications:", err);
      }

      // Refresh counts for all statuses
      loadAllGroupAppsCounts();

      // Show success message
      setSuccessMessage("Rejected delete request successfully");
      setOpenSuccessPopup(true);
    } catch (error) {
      setErrorMessage(error?.message || "Failed to reject delete request");
      setOpenErrorPopup(true);
    } finally {
      setRejectLoading(false);
      setRejectConfirmDialog({
        open: false,
        applicationId: null,
        applicationName: "",
      });
    }
  };

  // Handle reject cancel
  const handleRejectCancel = () => {
    setRejectConfirmDialog({
      open: false,
      applicationId: null,
      applicationName: "",
    });
  };

  const findRelevantSubmission = (submissionsArray, status) =>
    submissionsArray.find((sub) => {
      const latestStatusChange = sub.changelog?.[sub.changelog.length - 1];
      return latestStatusChange?.toState === status;
    }) || submissionsArray[0];

  // Helper function to normalize submissions - convert single object to array and group by application ID
  const normalizeAndGroupApplications = (applications) => {
    if (!applications || !Array.isArray(applications)) {
      return [];
    }

    // First, normalize submissions to array format
    const normalizedApps = applications.map((app) => {
      // Convert submissions from single object to array if needed
      let submissionsArray = [];
      if (app.submissions) {
        // If submissions is an object, convert to array
        if (typeof app.submissions === 'object' && !Array.isArray(app.submissions)) {
          submissionsArray = [app.submissions];
        } else if (Array.isArray(app.submissions)) {
          submissionsArray = app.submissions;
        }
      }

      return {
        ...app,
        submissions: submissionsArray,
      };
    });

    // Second, group by application ID to consolidate duplicates
    const groupedApps = {};
    normalizedApps.forEach((app) => {
      const appId = app.id;
      if (!groupedApps[appId]) {
        groupedApps[appId] = {
          ...app,
          submissions: [],
        };
      }
      // Merge submissions from duplicate entries
      if (app.submissions && Array.isArray(app.submissions)) {
        groupedApps[appId].submissions = [
          ...groupedApps[appId].submissions,
          ...app.submissions,
        ];
      }
    });

    return Object.values(groupedApps);
  };

  const getAllApplications = useCallback(
    function (status, skipLoader = false) {
      // Prevent multiple simultaneous calls
      if (loader && !skipLoader) {
        return;
      }

      if (!skipLoader) {
        setLoader(true);
      }
      setShowNoAppImage(false);
      setErrorMessage("");

      if (status === "DELETED") {
        dispatch(fetchDeletedApplications());
        if (!skipLoader) {
          setLoader(false);
        }
        return;
      }

      APIManager.getAllApplication(status)
        .then((json) => {
          setApplicationList([]);

          if (json && json.length > 0) {
            if (json[0].message === undefined) {
              // Normalize submissions and group by application ID
              const groupedApplications = normalizeAndGroupApplications(json);

              // Process applications and find the right submission for the status
              const processedApplications = groupedApplications.map((app, index) => {
                const submissionsArray = Array.isArray(app.submissions) ? app.submissions : [];
                return {
                  ...app,
                  id: app.id || app.applicationId || index + 1,
                  currentSubmission: findRelevantSubmission(submissionsArray, status),
                };
              });

              setApplicationList(processedApplications);
              if (!skipLoader) {
                setLoader(false);
              }
            } else {
              setErrorMessage(json[0].message);
              setOpenErrorPopup(true);
              if (!skipLoader) {
                setLoader(false);
              }
            }
          } else {
            setShowNoAppImage(true);
            if (!skipLoader) {
              setLoader(false);
            }
          }
        })
        .catch((error) => {
          let errorMsg = "An error occurred";
          if (error.message) {
            errorMsg = error.message;
          } else if (error.detail) {
            errorMsg = `API Error: ${error.detail}`;
          }
          setErrorMessage(errorMsg);
          setOpenErrorPopup(true);
          if (!skipLoader) {
            setLoader(false);
          }
        });
    },
    [dispatch, loader]
  );

  // Function to switch status without making API calls (uses cached data)
  const switchToStatus = useCallback(
    (status) => {
      setShowNoAppImage(false);
      setErrorMessage("");

      if (status === "DELETED") {
        // DELETED status is handled separately via Redux
        setApplicationList(deletedApplications || []);
        if (!deletedApplications || deletedApplications.length === 0) {
          setShowNoAppImage(true);
        }
        return;
      }

      // Check if we have cached data for this status
      if (allApplicationsData[status]) {
        setApplicationList(allApplicationsData[status]);
        if (allApplicationsData[status].length === 0) {
          setShowNoAppImage(true);
        }
      } else {
        // If no cached data, fetch it (shouldn't happen normally after initial load)
        getAllApplications(status);
      }
    },
    [allApplicationsData, deletedApplications, getAllApplications]
  );

  // Load counts for all Group Apps statuses
  const loadAllGroupAppsCounts = useCallback(async () => {
    // Prevent multiple simultaneous calls using current state
    if (isCountsLoadingRef.current) {
      return;
    }

    isCountsLoadingRef.current = true;
    const statusesToCheck = [
      "SUBMITTED_FOR_BETA",
      "APPROVED_FOR_BETA",
      "SUBMITTED_FOR_STAGING",
      "APPROVED_FOR_STAGING",
      "IN_REVIEW",
      "SUBMITTED_FOR_PRODUCTION",
      "APPROVED_FOR_PRODUCTION",
      "REJECTED_FOR_PRODUCTION",
      "PUBLISHED",
      "GENERATING_DELTA",
      "DELISTED_BY_SELLER",
      "DELISTED_BY_ADMIN",
      "DELISTED_BY_APPROVER",
    ];

    const newCounts = {};

    try {
      const batchSize = 3;
      for (let i = 0; i < statusesToCheck.length; i += batchSize) {
        const batch = statusesToCheck.slice(i, i + batchSize);

        const batchPromises = batch.map(async (status) => {
          try {
            const response = await APIManager.getAllApplication(status);
            if (response && Array.isArray(response)) {
              newCounts[status] = response.length;
            } else {
              newCounts[status] = 0;
            }
          } catch (error) {
            newCounts[status] = 0;
            console.error(`Failed to fetch count for status ${status}:`, error);
          }
        });

        await Promise.all(batchPromises);

        if (i + batchSize < statusesToCheck.length) {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
      }

      try {
        const deletedAppsResponse = await APIManager.getDeletedApplications();
        newCounts.DELETED = deletedAppsResponse
          ? deletedAppsResponse.length
          : 0;
      } catch (error) {
        newCounts.DELETED = 0;
        console.error("Failed to fetch deleted applications count:", error);
      }

      setGroupAppsCounts(newCounts);
    } catch (error) {
      console.error("Failed to load group apps counts:", error);
    } finally {
      isCountsLoadingRef.current = false;
    }
  }, [deletedApplications]);

  const [groupAppsCounts, setGroupAppsCounts] = useState({
    SUBMITTED_FOR_BETA: 0,
    APPROVED_FOR_BETA: 0,
    SUBMITTED_FOR_STAGING: 0,
    APPROVED_FOR_STAGING: 0,
    IN_REVIEW: 0,
    SUBMITTED_FOR_PRODUCTION: 0,
    APPROVED_FOR_PRODUCTION: 0,
    REJECTED_FOR_PRODUCTION: 0,
    PUBLISHED: 0,
    GENERATING_DELTA: 0,
    DELETED: 0,
    DELISTED_BY_SELLER: 0,
    DELISTED_BY_ADMIN: 0,
    DELISTED_BY_APPROVER: 0,
  });

  const processThirdPartyData = useCallback(() => {
    if (domainApproverContents && domainApproverContents.length > 0) {
      const groupedByStatus = {
        DOMAIN_APPROVAL_IN_REVIEW: [],
        DOMAIN_APPROVAL_APPROVED: [],
        REJECTED_BY_DOMAIN_APPROVER: [],
      };

      domainApproverContents.forEach((app, index) => {
        const status = app.domainApprovalStatus;

        // Create a processed application object
        const processedApp = {
          ...app,
          id: app.id || app.applicationId || index + 1,
        };

        if (
          status === "DOMAIN_APPROVAL_APPROVED" ||
          status === "APPROVED_BY_DOMAIN_APPROVER"
        ) {
          groupedByStatus.DOMAIN_APPROVAL_APPROVED.push(processedApp);
        } else if (status === "REJECTED_BY_DOMAIN_APPROVER") {
          groupedByStatus.REJECTED_BY_DOMAIN_APPROVER.push(processedApp);
        } else if (status === "DOMAIN_APPROVAL_IN_REVIEW") {
          groupedByStatus.DOMAIN_APPROVAL_IN_REVIEW.push(processedApp);
        } else {
          groupedByStatus.DOMAIN_APPROVAL_IN_REVIEW.push(processedApp);
        }
      });

      const currentStatusApps = groupedByStatus[activeThirdPartyStatus] || [];
      setThirdPartyApplications(currentStatusApps);
    } else {
      setThirdPartyApplications([]);
    }
  }, [domainApproverContents, activeThirdPartyStatus]);

  const loadInitialData = useCallback(async () => {
    loaderStartTime.current = Date.now();
    setLoader(true);
    setIsDataLoaded(false);
    setShowNoAppImage(false);
    setErrorMessage("");

    try {
      // Load counts for all statuses - call each API only once
      const statusesToCheck = [
        "IN_REVIEW",
        "SUBMITTED_FOR_PRODUCTION",
        "APPROVED_FOR_PRODUCTION",
        "REJECTED_FOR_PRODUCTION",
        "PUBLISHED",
        "GENERATING_DELTA",
        "DELISTED_BY_SELLER",
        "DELISTED_BY_ADMIN",
        "DELISTED_BY_APPROVER",
      ];

      // Collect all publisher IDs in a local variable
      let allPublisherIds = [];
      const allAppsData = {}; // Store all applications by status

      const processStatusResponse = (status, response) => {
        const publisherIds = response.map((app) => app.publisherId);
        const count = Array.isArray(response) ? response.length : 0;

        if (!response || response.length === 0) {
          allAppsData[status] = [];
          if (status === "SUBMITTED_FOR_PRODUCTION") {
            setShowNoAppImage(true);
            setApplicationList([]);
          }
          return { publisherIds: [], count: 0 };
        }

        if (response[0].message !== undefined) {
          allAppsData[status] = [];
          if (status === "SUBMITTED_FOR_PRODUCTION") {
            setErrorMessage(response[0].message);
            setOpenErrorPopup(true);
            setApplicationList([]);
          }
          return { publisherIds, count };
        }

        const groupedApplications = normalizeAndGroupApplications(response);
        const processedApplications = groupedApplications.map((app, index) => {
          const submissionsArray = Array.isArray(app.submissions) ? app.submissions : [];
          return {
            ...app,
            id: app.id || app.applicationId || index + 1,
            currentSubmission: findRelevantSubmission(submissionsArray, status),
          };
        });
        allAppsData[status] = processedApplications;
        if (status === "SUBMITTED_FOR_PRODUCTION") {
          setApplicationList(processedApplications);
        }
        return { publisherIds, count };
      };

      const countPromises = statusesToCheck.map(async (status) => {
        try {
          const response = await APIManager.getAllApplication(status);
          const { publisherIds, count } = processStatusResponse(status, response);
          allPublisherIds = [...allPublisherIds, ...publisherIds];
          return { status, count };
        } catch (error) {
          console.error(`Error fetching ${status}:`, error);
          allAppsData[status] = [];
          return { status, count: 0 };
        }
      });

      const countResults = await Promise.all(countPromises);

      // Store all applications data in state
      setAllApplicationsData(allAppsData);

      const newCounts = {};
      countResults.forEach(({ status, count }) => {
        newCounts[status] = count;
      });

      const currentDeletedApps = deletedApplications || [];
      newCounts.DELETED = currentDeletedApps.length;

      setGroupAppsCounts(newCounts);

      // Remove duplicates from collected publisher IDs
      const uniquePublisherIds = [...new Set(allPublisherIds)];

      // Fetch cognito users from Redux cache
      if (uniquePublisherIds.length > 0) {
        try {
          // Dispatch fetchCognitoUsers to get cached or fresh data
          const users = dispatch(fetchCognitoUsers());
          
          if (users && Array.isArray(users)) {
            // Create a map of user ID to firstName from the cached cognito users
            const publisherIdNameMap = users.map((user) => ({
              publisherId: user.id,
              publisherName: user.firstName,
            }));

            setPublisherIdName(publisherIdNameMap);
          }
        } catch (error) {
          console.error("Error fetching cognito users:", error);
        }
      }

      setIsDataLoaded(true);
    } catch (error) {
      console.error("Error in loadInitialData:", error);
      setIsDataLoaded(true);
    }
  }, [deletedApplications]);

  useEffect(() => {
    const isReturningFromDetails =
      sessionStorage.getItem("globalAdminNavigatingToDetails") === "true";
    if (isReturningFromDetails) {
      sessionStorage.removeItem("globalAdminNavigatingToDetails");
    }

    if (!hasLoadedInitialData.current) {
      hasLoadedInitialData.current = true;
      loadInitialData();
    }
    return () => {
      const isNavigatingToDetails =
        sessionStorage.getItem("globalAdminNavigatingToDetails") === "true";
      if (!isNavigatingToDetails) {
        sessionStorage.removeItem("globalAdminActiveStatus");
      }
    };
  }, []);

  useEffect(() => {
    if (
      !loader &&
      hasLoadedInitialData.current &&
      Object.keys(allApplicationsData).length > 0
    ) {
      switchToStatus(activeStatus);
    }
  }, [loader, allApplicationsData]);

  // Effect to hide loader with minimum display duration
  useEffect(() => {
    if (isDataLoaded && loaderStartTime.current) {
      const hideLoader = () => {
        setLoader(false);
        loaderStartTime.current = null;
      };

      const elapsedTime = Date.now() - loaderStartTime.current;
      const minLoaderDuration = 500; // minimum 500ms

      if (elapsedTime < minLoaderDuration) {
        const remainingTime = minLoaderDuration - elapsedTime;
        setTimeout(hideLoader, remainingTime);
      } else {
        hideLoader();
      }
    }
  }, [isDataLoaded]);

  useEffect(() => {
    if (deletedApplications !== null && !loader) {
      const timeoutId = setTimeout(() => {
        setGroupAppsCounts((prev) => ({
          ...prev,
          DELETED: deletedApplications.length,
        }));
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [deletedApplications, loader]);

  useEffect(() => {
    dispatch(fetchDeletedApplications());
  }, [dispatch]);

  // Fetch filter data (countries and ECU types) on component mount with Redux caching
  // Also set up 5-minute auto-refresh for filter data
  useEffect(() => {
    const fetchFilterData = () => {
      // Dispatch Redux actions which handle caching internally
      dispatch(fetchDomainApproverCountriesDetails());
      dispatch(fetchDomainApproverEcuDetails());
    };

    // Fetch on component mount
    fetchFilterData();

    // Set up 5-minute auto-refresh for filter data (countries and ECU types)
    const REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes in milliseconds
    
    filterDataRefreshIntervalRef.current = setInterval(() => {
      // Force fresh data by clearing cache and fetching new data
      dispatch(forceFreshCountriesDetails());
      dispatch(forceFreshEcuDetails());
      console.log("Filter data refreshed at", new Date().toLocaleTimeString());
    }, REFRESH_INTERVAL);

    // Cleanup interval on component unmount
    return () => {
      if (filterDataRefreshIntervalRef.current) {
        clearInterval(filterDataRefreshIntervalRef.current);
      }
    };
  }, [dispatch]);

  useEffect(() => {
    if (selectedTab === 1) {
      setOpenSections((prev) => ({
        ...prev,
        Production: true,
      }));
    }
  }, [selectedTab]);

  const staticSections = {
    "3rd Party Apps": {
      Production: [
        {
          status: "SUBMITTED_FOR_PRODUCTION",
          text: "Submitted",
          count: groupAppsCounts.SUBMITTED_FOR_PRODUCTION,
          isCountNeeded: true,
          icon: (
            <svg
              width="13"
              height="16"
              viewBox="0 0 13 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5.6 13.6H7.2V10.26L8.48 11.54L9.6 10.4L6.4 7.2L3.2 10.4L4.34 11.52L5.6 10.26V13.6ZM1.6 16C1.16 16 0.783333 15.8433 0.47 15.53C0.156667 15.2167 0 14.84 0 14.4V1.6C0 1.16 0.156667 0.783333 0.47 0.47C0.783333 0.156667 1.16 0 1.6 0H8L12.8 4.8V14.4C12.8 14.84 12.6433 15.2167 12.33 15.53C12.0167 15.8433 11.64 16 11.2 16H1.6ZM7.2 5.6H11.2L7.2 1.6V5.6Z"
                fill="#121415"
              />
            </svg>
          ),
        },
        {
          status: "IN_REVIEW",
          text: "In Review",
          count: groupAppsCounts.IN_REVIEW,
          isCountNeeded: true,
          icon: (
            <svg
              width="14"
              height="16"
              viewBox="0 0 14 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M2.2001 16C1.7601 16 1.38343 15.8433 1.0701 15.53C0.756764 15.2167 0.600098 14.84 0.600098 14.4V1.6C0.600098 1.16 0.756764 0.783333 1.0701 0.47C1.38343 0.156667 1.7601 0 2.2001 0H9.4001L13.4001 4.8V14.12L9.7601 10.4C9.90676 10.1733 10.0168 9.92667 10.0901 9.66C10.1634 9.39333 10.2001 9.10667 10.2001 8.8C10.2001 7.92 9.88676 7.16667 9.2601 6.54C8.63343 5.91333 7.8801 5.6 7.0001 5.6C6.1201 5.6 5.36676 5.91333 4.7401 6.54C4.11343 7.16667 3.8001 7.92 3.8001 8.8C3.8001 9.68 4.11343 10.4333 4.7401 11.06C5.36676 11.6867 6.1201 12 7.0001 12C7.30676 12 7.59343 11.9633 7.8601 11.89C8.12676 11.8167 8.37343 11.7067 8.6001 11.56L12.7601 15.68C12.4534 15.8133 12.1334 15.9 11.8001 15.94C11.4668 15.98 11.1334 16 10.8001 16H2.2001ZM7.0001 10.4C6.5601 10.4 6.18343 10.2433 5.8701 9.93C5.55676 9.61667 5.4001 9.24 5.4001 8.8C5.4001 8.36 5.55676 7.98333 5.8701 7.67C6.18343 7.35667 6.5601 7.2 7.0001 7.2C7.4401 7.2 7.81676 7.35667 8.1301 7.67C8.44343 7.98333 8.6001 8.36 8.6001 8.8C8.6001 9.24 8.44343 9.61667 8.1301 9.93C7.81676 10.2433 7.4401 10.4 7.0001 10.4Z"
                fill="#0476E2"
              />
            </svg>
          ),
        },
        {
          status: "APPROVED_FOR_PRODUCTION",
          text: "Approved",
          count: groupAppsCounts.APPROVED_FOR_PRODUCTION,
          isCountNeeded: true,
          icon: (
            <svg
              width="17"
              height="16"
              viewBox="0 0 17 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5.90962 16L4.462 13.5619L1.71914 12.9524L1.98581 10.1333L0.119141 8L1.98581 5.86667L1.71914 3.04762L4.462 2.4381L5.90962 0L8.50009 1.10476L11.0906 0L12.5382 2.4381L15.281 3.04762L15.0144 5.86667L16.881 8L15.0144 10.1333L15.281 12.9524L12.5382 13.5619L11.0906 16L8.50009 14.8952L5.90962 16ZM7.70009 10.7048L12.0049 6.4L10.9382 5.29524L7.70009 8.53333L6.062 6.93333L4.99533 8L7.70009 10.7048Z"
                fill="#121415"
              />
            </svg>
          ),
        },
        {
          status: "PUBLISHED",
          text: "Published",
          count: groupAppsCounts.PUBLISHED,
          isCountNeeded: true,
          icon: (
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M0.222078 6.632L3.62127 3.23281C3.81011 3.04396 4.03268 2.90908 4.28896 2.82814C4.54525 2.74721 4.80829 2.73372 5.07806 2.78768L6.13019 3.01024C5.40179 3.87353 4.82852 4.65588 4.41036 5.3573C3.99221 6.05872 3.58754 6.90852 3.19637 7.90669L0.222078 6.632ZM4.3699 8.47322C4.68014 7.50203 5.10167 6.58479 5.63448 5.7215C6.16729 4.85821 6.81138 4.04888 7.56675 3.29351C8.75377 2.10649 10.1094 1.2196 11.6336 0.632833C13.1579 0.0460683 14.581 -0.132659 15.9029 0.0966515C16.1322 1.41856 15.9568 2.84163 15.3768 4.36587C14.7968 5.89011 13.9133 7.24574 12.7262 8.43276C11.9844 9.17464 11.175 9.81874 10.2982 10.365C9.42147 10.9113 8.49748 11.3396 7.52629 11.6498L4.3699 8.47322ZM9.95428 6.04523C10.2645 6.35548 10.6456 6.5106 11.0975 6.5106C11.5493 6.5106 11.9304 6.35548 12.2406 6.04523C12.5509 5.73499 12.706 5.35393 12.706 4.90205C12.706 4.45018 12.5509 4.06912 12.2406 3.75887C11.9304 3.44863 11.5493 3.29351 11.0975 3.29351C10.6456 3.29351 10.2645 3.44863 9.95428 3.75887C9.64404 4.06912 9.48891 4.45018 9.48891 4.90205C9.48891 5.35393 9.64404 5.73499 9.95428 6.04523ZM9.38775 15.7774L8.09282 12.8031C9.09099 12.412 9.94416 12.0073 10.6523 11.5891C11.3605 11.171 12.1462 10.5977 13.0095 9.86932L13.2118 10.9214C13.2658 11.1912 13.2523 11.4576 13.1714 11.7207C13.0904 11.9837 12.9555 12.2096 12.7667 12.3985L9.38775 15.7774ZM1.51701 11.0631C1.98912 10.591 2.56239 10.3515 3.23683 10.3448C3.91128 10.3381 4.48455 10.5707 4.95666 11.0428C5.42877 11.515 5.66483 12.0882 5.66483 12.7627C5.66483 13.4371 5.42877 14.0104 4.95666 14.4825C4.61944 14.8197 4.05628 15.1097 3.26718 15.3525C2.47809 15.5953 1.38886 15.8112 -0.000488281 16C0.188356 14.6106 0.404177 13.5248 0.646976 12.7424C0.889776 11.9601 1.17979 11.4003 1.51701 11.0631Z"
                fill="#121415"
              />
            </svg>
          ),
        },
        {
          status: "REJECTED_FOR_PRODUCTION",
          text: "Rejected",
          count: groupAppsCounts.REJECTED_FOR_PRODUCTION,
          isCountNeeded: true,
          icon: (
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5.12 12L8 9.12L10.88 12L12 10.88L9.12 8L12 5.12L10.88 4L8 6.88L5.12 4L4 5.12L6.88 8L4 10.88L5.12 12ZM8 16C6.89333 16 5.85333 15.79 4.88 15.37C3.90667 14.95 3.06 14.38 2.34 13.66C1.62 12.94 1.05 12.0933 0.63 11.12C0.21 10.1467 0 9.10667 0 8C0 6.89333 0.21 5.85333 0.63 4.88C1.05 3.90667 1.62 3.06 2.34 2.34C3.06 1.62 3.90667 1.05 4.88 0.63C5.85333 0.21 6.89333 0 8 0C9.10667 0 10.1467 0.21 11.12 0.63C12.0933 1.05 12.94 1.62 13.66 2.34C14.38 3.06 14.95 3.90667 15.37 4.88C15.79 5.85333 16 6.89333 16 8C16 9.10667 15.79 10.1467 15.37 11.12C14.95 12.0933 14.38 12.94 13.66 13.66C12.94 14.38 12.0933 14.95 11.12 15.37C10.1467 15.79 9.10667 16 8 16Z"
                fill="#121415"
              />
            </svg>
          ),
        },
        {
          status: "GENERATING_DELTA",
          text: "Generating Delta",
          count: groupAppsCounts.GENERATING_DELTA || 0,
          isCountNeeded: true,
          icon: (
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6.49423 16V12.9129L12.0284 7.41647C12.1789 7.26588 12.3452 7.15294 12.5272 7.07765C12.7091 7.00235 12.9005 6.96471 13.1013 6.96471C13.3021 6.96471 13.4934 7.00235 13.6754 7.07765C13.8574 7.15294 14.0236 7.26588 14.1742 7.41647L15.0966 8.35765C15.2346 8.50824 15.3413 8.67451 15.4166 8.85647C15.4919 9.03843 15.5295 9.22353 15.5295 9.41177C15.5295 9.6 15.4887 9.7851 15.4072 9.96706C15.3256 10.149 15.2158 10.3153 15.0778 10.4659L9.58129 16H6.49423ZM13.0636 10.3906L14.0236 9.41177L13.1013 8.47059L12.1225 9.44941L13.0636 10.3906ZM7.05894 6.4L6.00482 5.32706L7.37894 3.95294H4.85659C4.74364 4.27922 4.56796 4.56157 4.32953 4.8C4.0911 5.03843 3.80874 5.21412 3.48247 5.32706V11.6141C3.92168 11.7773 4.28247 12.0502 4.56482 12.4329C4.84717 12.8157 4.98835 13.2518 4.98835 13.7412C4.98835 14.3686 4.76874 14.902 4.32953 15.3412C3.89031 15.7804 3.35698 16 2.72953 16C2.10208 16 1.56874 15.7804 1.12953 15.3412C0.690311 14.902 0.470703 14.3686 0.470703 13.7412C0.470703 13.2518 0.61188 12.8188 0.894233 12.4424C1.17659 12.0659 1.53737 11.7961 1.97659 11.6329V5.32706C1.53737 5.16392 1.17659 4.89098 0.894233 4.50824C0.61188 4.12549 0.470703 3.68941 0.470703 3.2C0.470703 2.57255 0.690311 2.03922 1.12953 1.6C1.56874 1.16078 2.10208 0.941176 2.72953 0.941176C3.21894 0.941176 3.65502 1.08235 4.03776 1.36471C4.42051 1.64706 4.69345 2.00784 4.85659 2.44706H7.37894L6.00482 1.07294L7.05894 0L10.2589 3.2L7.05894 6.4ZM13.2707 0.941176C13.8982 0.941176 14.4315 1.16078 14.8707 1.6C15.3099 2.03922 15.5295 2.57255 15.5295 3.2C15.5295 3.82745 15.3099 4.36078 14.8707 4.8C14.4315 5.23922 13.8982 5.45882 13.2707 5.45882C12.6433 5.45882 12.1099 5.23922 11.6707 4.8C11.2315 4.36078 11.0119 3.82745 11.0119 3.2C11.0119 2.57255 11.2315 2.03922 11.6707 1.6C12.1099 1.16078 12.6433 0.941176 13.2707 0.941176Z"
                fill="#121415"
              />
            </svg>
          ),
        },
      ],
      Deleted: [
        {
          status: "DELETED",
          text: "Deleted",
          count: groupAppsCounts.DELETED,
          isCountNeeded: true,
          icon: (
            <svg
              width="15"
              height="16"
              viewBox="0 0 15 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3.05534 16C2.56645 16 2.14793 15.8259 1.79978 15.4778C1.45163 15.1296 1.27756 14.7111 1.27756 14.2222V2.66667H0.388672V0.888889H4.83312V0H10.1664V0.888889H14.6109V2.66667H13.722V14.2222C13.722 14.7111 13.5479 15.1296 13.1998 15.4778C12.8516 15.8259 12.4331 16 11.9442 16H3.05534ZM11.9442 2.66667H3.05534V14.2222H11.9442V2.66667ZM4.83312 12.4444H6.61089V4.44444H4.83312V12.4444ZM8.38867 12.4444H10.1664V4.44444H8.38867V12.4444Z"
                fill="#0476E2"
              />
            </svg>
          ),
        },
      ],
      Delisted: [
        {
          status: "DELISTED_BY_SELLER",
          text: "By Seller",
          count: groupAppsCounts.DELISTED_BY_SELLER,
          isCountNeeded: true,
          icon: (
            <svg
              width="17"
              height="16"
              viewBox="0 0 17 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8.5 8C8.5 6.89697 8.89091 5.95455 9.67273 5.17273C10.4545 4.39091 11.397 4 12.5 4C13.603 4 14.5455 4.39091 15.3273 5.17273C16.1091 5.95455 16.5 6.89697 16.5 8H8.5ZM4.5 12C3.39697 12 2.45455 11.6091 1.67273 10.8273C0.890909 10.0455 0.5 9.10303 0.5 8H8.5C8.5 9.10303 8.10909 10.0455 7.32727 10.8273C6.54545 11.6091 5.60303 12 4.5 12ZM8.5 8C7.39697 8 6.45455 7.60909 5.67273 6.82727C4.89091 6.04545 4.5 5.10303 4.5 4C4.5 2.89697 4.89091 1.95455 5.67273 1.17273C6.45455 0.390909 7.39697 0 8.5 0V8ZM8.5 16V8C9.60303 8 10.5455 8.39091 11.3273 9.17273C12.1091 9.95455 12.5 10.897 12.5 12C12.5 13.103 12.1091 14.0455 11.3273 14.8273C10.5455 15.6091 9.60303 16 8.5 16Z"
                fill="#121415"
              />
            </svg>
          ),
        },
        {
          status: "DELISTED_BY_ADMIN",
          text: "By Admin",
          count: groupAppsCounts.DELISTED_BY_ADMIN,
          isCountNeeded: true,
          icon: (
            <svg
              width="17"
              height="16"
              viewBox="0 0 17 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8.5 8C8.5 6.89697 8.89091 5.95455 9.67273 5.17273C10.4545 4.39091 11.397 4 12.5 4C13.603 4 14.5455 4.39091 15.3273 5.17273C16.1091 5.95455 16.5 6.89697 16.5 8H8.5ZM4.5 12C3.39697 12 2.45455 11.6091 1.67273 10.8273C0.890909 10.0455 0.5 9.10303 0.5 8H8.5C8.5 9.10303 8.10909 10.0455 7.32727 10.8273C6.54545 11.6091 5.60303 12 4.5 12ZM8.5 8C7.39697 8 6.45455 7.60909 5.67273 6.82727C4.89091 6.04545 4.5 5.10303 4.5 4C4.5 2.89697 4.89091 1.95455 5.67273 1.17273C6.45455 0.390909 7.39697 0 8.5 0V8ZM8.5 16V8C9.60303 8 10.5455 8.39091 11.3273 9.17273C12.1091 9.95455 12.5 10.897 12.5 12C12.5 13.103 12.1091 14.0455 11.3273 14.8273C10.5455 15.6091 9.60303 16 8.5 16Z"
                fill="#121415"
              />
            </svg>
          ),
        },
        {
          status: "DELISTED_BY_APPROVER",
          text: "By Approver",
          count: groupAppsCounts.DELISTED_BY_APPROVER,
          isCountNeeded: true,
          icon: (
            <svg
              width="17"
              height="16"
              viewBox="0 0 17 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8.5 8C8.5 6.89697 8.89091 5.95455 9.67273 5.17273C10.4545 4.39091 11.397 4 12.5 4C13.603 4 14.5455 4.39091 15.3273 5.17273C16.1091 5.95455 16.5 6.89697 16.5 8H8.5ZM4.5 12C3.39697 12 2.45455 11.6091 1.67273 10.8273C0.890909 10.0455 0.5 9.10303 0.5 8H8.5C8.5 9.10303 8.10909 10.0455 7.32727 10.8273C6.54545 11.6091 5.60303 12 4.5 12ZM8.5 8C7.39697 8 6.45455 7.60909 5.67273 6.82727C4.89091 6.04545 4.5 5.10303 4.5 4C4.5 2.89697 4.89091 1.95455 5.67273 1.17273C6.45455 0.390909 7.39697 0 8.5 0V8ZM8.5 16V8C9.60303 8 10.5455 8.39091 11.3273 9.17273C12.1091 9.95455 12.5 10.897 12.5 12C12.5 13.103 12.1091 14.0455 11.3273 14.8273C10.5455 15.6091 9.60303 16 8.5 16Z"
                fill="#121415"
              />
            </svg>
          ),
        },
      ],
    },
  };

  const getPricingText = (row) => {
    const isPremium = row.isPremiumApp || false;
    const isMonetized = row.isMonetized || false;
    const isInAppPurchase = row.isInAppPurchase || false;
    if (!isMonetized) return "N/A";
    if (isPremium) return "Premium";
    if (isInAppPurchase && !isPremium) return "Free";
    return "N/A";
  };

  const renderGroupPricingCell = (row) => (
    <Typography
      sx={{
        fontSize: "14px",
        color: "#5A5B5C",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        width: "100%",
      }}
    >
      {getPricingText(row)}
    </Typography>
  );

  const columns = [
    {
      field: "icon",
      headerName: "",
      width: 60,
      sortable: false,
      renderHeader: () => null,
      renderCell: (params) => {
        const { row } = params;
        const iconUrl = row.iconUrl || row.currentSubmission?.iconUrl;

        if (!iconUrl) return null;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
            }}
          >
            <img
              src={iconUrl}
              alt="App Icon"
              style={{
                width: "32px",
                height: "32px",
                borderRadius: "4px",
                objectFit: "cover",
              }}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          </Box>
        );
      },
    },
    {
      field: "applicationName",
      headerName: "Application Name",
      flex: 1,
      minWidth: 170,
      sortable: false,
      renderHeader: () => (
        <Box
          sx={{ textAlign: "center", fontSize: "14px" }}
          onMouseEnter={() => setHoveredColumn("applicationName")}
          onMouseLeave={() => setHoveredColumn(null)}
        >
          Application Name
          <IconButton
            sx={{
              cursor: "pointer",
              borderRadius: "4px",
              marginLeft: "8px",
              width: "24px",
              height: "24px",
              padding: "4px",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            }}
            onClick={() => handleSort("applicationName")}
          >
            {(() => {
              if (sortBy === "applicationName") {
                return sortOrder === "asc" ? (
                  <ArrowUpwardIcon
                    sx={{
                      color: "#12141599",
                      height: "16px",
                      width: "16px",
                      transition: "color 0.2s ease",
                    }}
                  />
                ) : (
                  <ArrowDownwardIcon
                    sx={{
                      color: "#12141599",
                      height: "16px",
                      width: "16px",
                      transition: "color 0.2s ease",
                    }}
                  />
                );
              }
              if (hoveredColumn === "applicationName") {
                return (
                  <ArrowUpwardIcon
                    sx={{
                      color: "#12141599",
                      height: "16px",
                      width: "16px",
                      opacity: 0.5,
                      transition: "opacity 0.2s ease",
                    }}
                  />
                );
              }
              return null;
            })()}
          </IconButton>
        </Box>
      ),
      renderCell: (params) => {
        const { row } = params;

        if (row.isGroup) {
          // Group header row - show application name only
          return (
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: "600",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                width: "100%",
              }}
              title={row.applicationName || "Unknown"}
            >
              {row.applicationName || "Unknown"}
            </Typography>
          );
        } else if (row.isChild) {
          return (
            <Typography sx={{ fontSize: "14px", fontWeight: "600" }}>
              {/* Empty for child rows */}
            </Typography>
          );
        } else {
          return (
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: "600",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                width: "100%",
              }}
              title={row.applicationName || "N/A"}
            >
              {row.applicationName || "N/A"}
            </Typography>
          );
        }
      },
    },
    {
      field: "version",
      headerName: "Version",
      flex: 1,
      minWidth: 80,
      sortable: false,
      renderHeader: () => (
        <Box sx={{ textAlign: "center", fontSize: "14px" }}>Version</Box>
      ),
      renderCell: (params) => {
        const { row } = params;

        return (
          <Typography
            sx={{
              fontSize: "14px",
              color: "#5A5B5C",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              width: "100%",
            }}
            title={row.version || "N/A"}
          >
            {row.version || "N/A"}
          </Typography>
        );
      },
    },
    {
      field: "publisherId",
      headerName: "Publisher",
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderHeader: () => (
        <Box sx={{ textAlign: "center", fontSize: "14px" }}>Publisher</Box>
      ),
      renderCell: (params) => {
        const { row } = params;
        const publisherId = row.publisherId || "";
        const publisherObj = publisherIdName.find(
          (item) => item.publisherId === publisherId
        );
        const publisherName = publisherObj ? publisherObj.publisherName : "N/A";

        if (row.isGroup) {
          return (
            <Typography
              sx={{
                fontSize: "14px",
                color: "#5A5B5C",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                width: "100%",
              }}
              title={publisherName}
            >
              {publisherName}
            </Typography>
          );
        }

        return (
          <Typography
            sx={{
              fontSize: "14px",
              color: "#5A5B5C",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              width: "100%",
            }}
            title={publisherName}
          >
            {publisherName}
          </Typography>
        );
      },
    },
    {
      field: "lastModified",
      headerName: "Last Modified",
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderHeader: () => (
        <Box
          sx={{ textAlign: "center", fontSize: "14px" }}
          onMouseEnter={() => setHoveredColumn("lastModified")}
          onMouseLeave={() => setHoveredColumn(null)}
        >
          Last Modified
          <IconButton
            sx={{
              cursor: "pointer",
              borderRadius: "4px",
              marginLeft: "8px",
              width: "24px",
              height: "24px",
              padding: "4px",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            }}
            onClick={() => handleSort("lastModified")}
          >
            {(() => {
              if (sortBy === "lastModified") {
                return sortOrder === "asc" ? (
                  <ArrowUpwardIcon
                    sx={{
                      color: "#12141599",
                      height: "16px",
                      width: "16px",
                      transition: "color 0.2s ease",
                    }}
                  />
                ) : (
                  <ArrowDownwardIcon
                    sx={{
                      color: "#12141599",
                      height: "16px",
                      width: "16px",
                      transition: "color 0.2s ease",
                    }}
                  />
                );
              }
              if (hoveredColumn === "lastModified") {
                return (
                  <ArrowUpwardIcon
                    sx={{
                      color: "#12141599",
                      height: "16px",
                      width: "16px",
                      opacity: 0.5,
                      transition: "opacity 0.2s ease",
                    }}
                  />
                );
              }
              return null;
            })()}
          </IconButton>
        </Box>
      ),
      renderCell: (params) => {
        const { row } = params;

        if (row.isGroup) {
          const lastModifiedAt =
            row.currentSubmission?.lastModified ||
            row.lastModified ||
            row.lastModified;

          if (lastModifiedAt) {
            return (
              <Typography sx={{ fontSize: "14px", color: "#5A5B5C" }}>
                {new Date(lastModifiedAt).toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                }) || "N/A"}
              </Typography>
            );
          }

          const dateObj = new Date(lastModifiedAt);
          if (Number.isNaN(dateObj)) {
            return (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                }}
                onClick={($event) => handleGroupToggle($event, row.id)}
              >
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: "#5A5B5C",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  Invalid Date
                </Typography>
                {row.isExpanded ? (
                  <KeyboardArrowDown
                    sx={{ fontSize: "16px", marginLeft: "8px" }}
                  />
                ) : (
                  <KeyboardArrowUp
                    sx={{ fontSize: "16px", marginLeft: "8px" }}
                  />
                )}
              </Box>
            );
          }

          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
              }}
              onClick={($event) => handleGroupToggle($event, row.id)}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {dateObj.toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })}
              </Typography>
              {row.isExpanded ? (
                <KeyboardArrowDown
                  sx={{ fontSize: "16px", marginLeft: "8px" }}
                />
              ) : (
                <KeyboardArrowUp sx={{ fontSize: "16px", marginLeft: "8px" }} />
              )}
            </Box>
          );
        }

        const lastModifiedAt =
          row.currentSubmission?.lastModified || row.lastModified;
        if (!lastModifiedAt) {
          return (
            <Typography
              sx={{
                fontSize: "14px",
                color: "#5A5B5C",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                width: "100%",
              }}
            >
              N/A
            </Typography>
          );
        }

        const dateObj = new Date(lastModifiedAt);
        if (Number.isNaN(dateObj)) {
          return (
            <Typography
              sx={{
                fontSize: "14px",
                color: "#5A5B5C",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                width: "100%",
              }}
            >
              Invalid Date
            </Typography>
          );
        }

        return (
          <Typography
            sx={{
              fontSize: "14px",
              color: "#5A5B5C",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              width: "100%",
            }}
          >
            {dateObj.toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })}
          </Typography>
        );
      },
    },
    {
      field: "isMonetized",
      headerName: "App Pricing",
      flex: 1,
      maxWidth: 1000,
      minWidth: 110,
      sortable: false,
      renderHeader: () => (
        <Box sx={{ textAlign: "center", fontSize: "14px" }}>App Pricing</Box>
      ),
      renderCell: (params) => {
        const { row } = params;

        if (row.isGroup) {
          return renderGroupPricingCell(row);
        }

        return (
          <Typography
            sx={{
              fontSize: "14px",
              color: "#5A5B5C",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              width: "100%",
            }}
          >
            {getPricingText(row)}
          </Typography>
        );
      },
    },
  ];

  const deletedColumns = [
    {
      field: "icon",
      headerName: "",
      width: 60,
      sortable: false,
      renderHeader: () => null,
      renderCell: (params) => {
        const { row } = params;
        const iconUrl = row.iconUrl || row.currentSubmission?.iconUrl;

        if (!iconUrl) return null;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img
              src={iconUrl}
              alt="App Icon"
              style={{
                width: "32px",
                height: "32px",
                borderRadius: "4px",
                objectFit: "cover",
              }}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          </Box>
        );
      },
    },
    {
      field: "applicationName",
      headerName: "Application Name",
      flex: 1,
      minWidth: 170,
      sortable: false,
      renderHeader: () => (
        <Box sx={{ textAlign: "center", fontSize: "14px" }}>
          Application Name
          <IconButton
            sx={{
              cursor: "pointer",
              borderRadius: "4px",
              marginLeft: "8px",
              width: "24px",
              height: "24px",
              padding: "4px",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            }}
            onClick={() => handleSort("applicationName")}
          >
            {(() => {
              if (sortBy === "applicationName") {
                return sortOrder === "asc" ? (
                  <ArrowUpwardIcon
                    sx={{
                      color: "#12141599",
                      height: "16px",
                      width: "16px",
                      transition: "color 0.2s ease",
                    }}
                  />
                ) : (
                  <ArrowDownwardIcon
                    sx={{
                      color: "#12141599",
                      height: "16px",
                      width: "16px",
                      transition: "color 0.2s ease",
                    }}
                  />
                );
              }
              return null;
            })()}
          </IconButton>
        </Box>
      ),
      renderCell: (params) => (
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 600,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            width: "100%",
          }}
          title={params.row.applicationName || "N/A"}
        >
          {params.row.applicationName || "N/A"}
        </Typography>
      ),
    },
    {
      field: "version",
      headerName: "Version",
      flex: 1,
      minWidth: 80,
      sortable: false,
      renderHeader: () => (
        <Box sx={{ textAlign: "center", fontSize: "14px" }}>Version</Box>
      ),
      renderCell: (params) => {
        return (
          <Box title={params.row.version}>
            <Typography
              sx={{
                fontSize: "14px",
                color: "#5A5B5C",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                width: "100%",
              }}
            >
              {params.row.version || "N/A"}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "publisherId",
      headerName: "Publisher",
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderHeader: () => (
        <Box sx={{ textAlign: "center", fontSize: "14px" }}>Publisher</Box>
      ),
      renderCell: (params) => {
        const { row } = params;

        if (row.isGroup) {
          return (
            <Typography
              sx={{
                fontSize: "14px",
                color: "#5A5B5C",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                width: "100%",
              }}
            >
              -
            </Typography>
          );
        }

        const publisherId = row.publisherId || "";
        const publisherName = publisherId.includes("_")
          ? publisherId.split("_")[0]
          : publisherId;

        return (
          <Box sx={{ paddingLeft: row.isChild ? "16px" : "0px" }}>
            <Typography
              sx={{
                fontSize: "14px",
                color: "#5A5B5C",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                width: "100%",
              }}
              title={publisherName}
            >
              {publisherName || "N/A"}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "deletedAt",
      headerName: "Deleted",
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderHeader: () => (
        <Box sx={{ textAlign: "center", fontSize: "14px" }}>
          Deleted
          <IconButton
            sx={{
              cursor: "pointer",
              borderRadius: "4px",
              marginLeft: "8px",
              width: "24px",
              height: "24px",
              padding: "4px",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            }}
            onClick={() => handleSort("deletedAt")}
          >
            {(() => {
              if (sortBy === "deletedAt") {
                return sortOrder === "asc" ? (
                  <ArrowUpwardIcon
                    sx={{
                      color: "#12141599",
                      height: "16px",
                      width: "16px",
                      transition: "color 0.2s ease",
                    }}
                  />
                ) : (
                  <ArrowDownwardIcon
                    sx={{
                      color: "#12141599",
                      height: "16px",
                      width: "16px",
                      transition: "color 0.2s ease",
                    }}
                  />
                );
              }
              return null;
            })()}
          </IconButton>
        </Box>
      ),
      renderCell: (params) => {
        const { row } = params;

        if (row.isGroup) {
          return (
            <Typography
              sx={{
                fontSize: "14px",
                color: "#5A5B5C",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                width: "100%",
              }}
            >
              -
            </Typography>
          );
        }

        // Use deletedAt, lastModified, or other date fields from the API response
        const deletedAt = row.deletedAt || row.lastModified || row.submittedAt;
        if (!deletedAt) {
          return (
            <Typography
              sx={{
                fontSize: "14px",
                color: "#5A5B5C",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                width: "100%",
              }}
            >
              N/A
            </Typography>
          );
        }

        const dateObj = new Date(deletedAt);
        if (Number.isNaN(dateObj)) {
          return (
            <Typography
              sx={{
                fontSize: "14px",
                color: "#5A5B5C",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                width: "100%",
              }}
            >
              Invalid Date
            </Typography>
          );
        }

        return (
          <Typography
            sx={{
              fontSize: "14px",
              color: "#5A5B5C",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              width: "100%",
            }}
          >
            {dateObj.toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })}
          </Typography>
        );
      },
    },
    {
      // field: "actions",
      // headerName: "Action",
      flex: 1,
      minWidth: 150,
      sortable: false,
      //  renderHeader: () => <Box sx={{ textAlign: "center" }}>Action</Box>,
      renderCell: (params) => {
        const { row } = params;

        if (row.isGroup) {
          // Group header - don't show actions
          return (
            <Typography
              sx={{ fontSize: "12px", color: "#2A2C2D", fontWeight: 500 }}
            >
              -
            </Typography>
          );
        }

        return (
          <Box
            sx={{
              display: "flex",
              gap: "8px",
              alignItems: "center",
            }}
            disabled={readOnly_GlobalAdmin}
          >
            <Button
              size="small"
              disabled={readOnly_GlobalAdmin || rejectLoading}
              sx={{
                fontSize: "10px",
                padding: "4px 8px",
                minWidth: "80px",
                textTransform: "none",
                color: "#2A2C2D",
                backgroundColor: "#1214151A",
                border: "1px solid #1214151A",
              }}
              onClick={() =>
                handleRejectClick(
                  row.id || row.applicationId,
                  row.applicationName
                )
              }
            >
              {rejectLoading ? "..." : "Cancel Deletion"}
            </Button>
            <Button
              size="small"
              sx={{
                fontSize: "10px",
                padding: "4px 8px",
                minWidth: "40px",
                textTransform: "none",
                color: "#B50B00",

                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              disabled={readOnly_GlobalAdmin}
              onClick={() =>
                handleDeleteClick(
                  row.id || row.applicationId,
                  row.applicationName
                )
              }
            >
              <svg
                width="15"
                height="17"
                viewBox="0 0 15 17"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3.05534 16.6667C2.56645 16.6667 2.14793 16.4927 1.79978 16.1445C1.45163 15.7964 1.27756 15.3779 1.27756 14.889V3.33341H0.388672V1.55564H4.83312V0.666748H10.1664V1.55564H14.6109V3.33341H13.722V14.889C13.722 15.3779 13.5479 15.7964 13.1998 16.1445C12.8516 16.4927 12.4331 16.6667 11.9442 16.6667H3.05534ZM11.9442 3.33341H3.05534V14.889H11.9442V3.33341ZM4.83312 13.1112H6.61089V5.11119H4.83312V13.1112ZM8.38867 13.1112H10.1664V5.11119H8.38867V13.1112Z"
                  fill="#B50B00"
                />
              </svg>
            </Button>
          </Box>
        );
      },
    },
  ];

  // Helper function to format status for display
  const formatStatusForDisplay = (status) => {
    const statusMap = {
      IN_REVIEW: "In Review",
      SUBMITTED_FOR_PRODUCTION: "Submitted for Production",
      APPROVED_FOR_PRODUCTION: "Approved for Production",
      REJECTED_FOR_PRODUCTION: "Rejected for Production",
      PUBLISHED: "Published",
      DELETED: "Deleted",
      DELISTED_BY_SELLER: "Delisted by Seller",
      DELISTED_BY_ADMIN: "Delisted by Admin",
      DELISTED_BY_APPROVER: "Delisted by Approver",
      GENERATING_DELTA: "Generating Delta",
    };
    return statusMap[status] || status;
  };

  const toggleSection = ($event, section) => {
    $event.stopPropagation();
    setOpenSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Filter applications based on search term
  const getFilteredApplications = () => {
    let dataSource;

    if (selectedTab === 0) {
      // Group Apps tab
      if (activeStatus === "DELETED") {
        dataSource = deletedApplications || [];
      } else {
        dataSource = applicationList;
      }
    } else {
      // 3rd Party Apps tab
      dataSource = thirdPartyApplications;
    }

    // Apply search filter first
    let filteredData = dataSource;
    if (searchTerm.trim()) {
      filteredData = dataSource.filter((app) => {
        const appName = app.applicationName || "";
        const publisherId = app.publisherId || "";
        const searchLower = searchTerm.toLowerCase();

        return (
          appName.toLowerCase().includes(searchLower) ||
          publisherId.toLowerCase().includes(searchLower)
        );
      });
    }

    // Group by application name and then sort
    const groupedApplications = groupApplicationsByName(filteredData);
    return sortApplications(groupedApplications);
  };

  const getParentRowCount = (applications) => {
    return applications.filter((app) => !app.isChild).length;
  };

  // Sort applications based on selected column and order while preserving parent-child structure
  const sortApplications = (applications) => {
    // Separate parent rows and child rows
    const parentRows = [];
    const childRowsMap = new Map(); // Map parentId to child rows

    applications.forEach((app) => {
      if (app.isChild) {
        const parentId = app.parentId;
        if (!childRowsMap.has(parentId)) {
          childRowsMap.set(parentId, []);
        }
        childRowsMap.get(parentId).push(app);
      } else {
        parentRows.push(app);
      }
    });

    // Sort parent rows based on selected criteria
    const sortedParents = parentRows.toSorted((a, b) => {
      let comparison = 0;

      if (sortBy === "applicationName") {
        // Sort by application name
        const nameA = (a.applicationName || "").toLowerCase();
        const nameB = (b.applicationName || "").toLowerCase();
        comparison = nameA.localeCompare(nameB);
      } else if (sortBy === "lastModified" || sortBy === "deletedAt") {
        // Sort by date (last modified or deleted date)
        const getDateForSorting = (app) => {
          let possibleDates = [];

          if (sortBy === "deletedAt") {
            possibleDates = [
              app.deletedAt,
              app.lastModified,
              app.submittedAt,
              app.currentSubmission?.lastModified,
              app.currentSubmission?.submittedAt,
              app.currentSubmission?.createdAt,
              app.createdAt,
            ];
          } else {
            possibleDates = [
              app.currentSubmission?.lastModified,
              app.lastModified,
              app.currentSubmission?.submittedAt,
              app.submittedAt,
              app.currentSubmission?.createdAt,
              app.createdAt,
            ];
          }

          // Find the first valid date
          for (const dateStr of possibleDates) {
            if (dateStr) {
              const date = new Date(dateStr);
              if (!Number.isNaN(date.getTime())) {
                return date;
              }
            }
          }

          // Return epoch time if no valid date found
          return new Date(0);
        };

        const dateA = getDateForSorting(a);
        const dateB = getDateForSorting(b);
        const timeA = dateA.getTime();
        const timeB = dateB.getTime();

        // Handle cases where both dates are invalid (epoch time)
        if (timeA === 0 && timeB === 0) {
          // Secondary sort by application name if dates are both invalid
          const nameA = (a.applicationName || "").toLowerCase();
          const nameB = (b.applicationName || "").toLowerCase();
          comparison = nameA.localeCompare(nameB);
        } else {
          comparison = timeA - timeB;
        }
      }

      // Apply sort order (ascending or descending)
      return sortOrder === "asc" ? comparison : -comparison;
    });

    // Reconstruct the array with parent rows followed by their children
    const result = [];
    sortedParents.forEach((parent) => {
      result.push(parent);

      // Add child rows immediately after parent if they exist
      const childRows = childRowsMap.get(parent.id);
      if (childRows && childRows.length > 0) {
        // Sort child rows by their original order or by submission order
        const sortedChildren = childRows.sort((a, b) => {
          // Sort children by version or submission date to maintain consistent order
          const versionA = a.version || "";
          const versionB = b.version || "";
          return versionA.localeCompare(versionB);
        });
        result.push(...sortedChildren);
      }
    });

    return result;
  };

  // Group applications by name and create hierarchical structure based on submissions
  const groupApplicationsByName = (applications) => {
    const result = [];

    // Process each application
    applications.forEach((app) => {
      const appName = app.applicationName || "Unknown";
      const submissions = app.submissions || [];

      // If application has multiple submissions, create a group
      if (submissions.length > 1) {
        const groupId = `group_${app.id}`;
        const isExpanded = expandedGroups.has(groupId);

        // Add first submission as the parent/group row with expand/collapse
        const firstSubmission = submissions[0];
        result.push({
          ...app, // Keep all app-level data
          id: groupId,
          applicationName: appName,
          version:
            firstSubmission.version || firstSubmission.name || "Version 1",
          isGroup: true,
          isExpanded,
          childCount: submissions.length - 1, // Count remaining submissions
          currentSubmission: firstSubmission, // Set the first submission
          submittedAt: firstSubmission.submittedAt || app.submittedAt,
          // Override app-level data with submission-specific data where applicable
          countries: firstSubmission.countries || app.countries,
          ecuTypes: firstSubmission.ecuTypes || app.ecuTypes,
          minSdkVersion: firstSubmission.minSdkVersion || app.minSdkVersion,
        });

        // Add remaining child submissions if expanded (skip the first one)
        if (isExpanded) {
          submissions.slice(1).forEach((submission, index) => {
            result.push({
              ...app, // Keep all app-level data
              id: `${app.id}_submission_${submission.id || index + 1}`,
              version:
                submission.version || submission.name || `Version ${index + 2}`,
              isGroup: false,
              isChild: true,
              groupName: appName,
              parentId: groupId,
              currentSubmission: submission, // Set the specific submission
              submittedAt: submission.submittedAt || app.submittedAt,
              // Override app-level data with submission-specific data where applicable
              countries: submission.countries || app.countries,
              ecuTypes: submission.ecuTypes || app.ecuTypes,
              minSdkVersion: submission.minSdkVersion || app.minSdkVersion,
            });
          });
        }
      } else {
        // Single submission - add directly without grouping
        const singleSubmission = submissions[0];
        result.push({
          ...app,
          isGroup: false,
          version:
            singleSubmission?.version || singleSubmission?.name || app.version,
          // Use the single submission as current submission
          currentSubmission: singleSubmission || null,
        });
      }
    });

    return result;
  };

  // Handle group expand/collapse
  const handleGroupToggle = ($event, groupId) => {
    $event.stopPropagation();
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  // Filter-related functions
  const toggleDrawer = (newOpen) => () => {
    setOpenFilter(newOpen);
  };

  const handleFilterApiCalls = (data) => {
    // Reset pagination when filters are applied
    resetPagination();
    // Filter functionality can be implemented here if needed
  };

  // Get appropriate columns based on current state
  const getCurrentColumns = () => {
    if (selectedTab === 0 && activeStatus === "DELETED") {
      return deletedColumns;
    }
    return columns;
  };

  return (
    <>
      <Topheader />

      {/* Loader - Show immediately when loading data */}
      {loader && (
        <Backdrop
          sx={(theme) => ({
            color: "#fff",
            zIndex: theme.zIndex.drawer + 999,
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
          })}
          open={loader}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}

      {/* Main UI - Show only when data is loaded */}
      {!loader && (
        <>
          <Toolbar />
          <Box sx={{ display: "flex" }}>
            <Box
              sx={{
                width: "240px",
                flexShrink: 0,
                paddingLeft: "75px",
                marginTop: "24px",
              }}
            >
              {/* Sidebar menu - always show for Group Apps if idpLogin is false, otherwise show based on selected tab */}
              {Object.entries(staticSections["3rd Party Apps"]).map(
                ([label, items]) => (
                  <Box key={label} sx={{ marginBottom: "8px" }}>
                    <ListItem
                      button
                      onClick={($event) => toggleSection($event, label)}
                      sx={{
                        padding: "4px 16px",
                        borderRadius: "4px",
                        marginBottom: "4px",
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography
                            sx={{
                              fontSize: "13px",
                              fontWeight: "600",
                              color: "#2A2C2D",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              width: "100%",
                              cursor: "pointer",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <ArrowDropUpRounded
                                sx={{
                                  transform: openSections[label]
                                    ? "rotate(0deg)"
                                    : "rotate(180deg)",
                                  transition: "transform 0.3s",
                                  color: "#12141599",
                                }}
                              />
                              {label}
                            </Box>
                            <Typography
                              sx={{
                                fontSize: "11px",
                                fontWeight: "600",
                                color: "#2A2C2D",
                              }}
                            >
                              {items.reduce(
                                (total, item) => total + (item.count || 0),
                                0
                              )}
                            </Typography>
                          </Typography>
                        }
                      />
                    </ListItem>
                    <Collapse
                      in={openSections[label]}
                      timeout="auto"
                      unmountOnExit
                    >
                      <List component="div" disablePadding>
                        {items.map((item) => (
                          <ListItem
                            key={item.status}
                            button
                            onClick={() => {
                              if (!idpLogin || selectedTab === 0) {
                                // Group Apps tab (or when idpLogin is false, always treat as Group Apps)
                                setActiveStatus(item.status);
                                sessionStorage.setItem(
                                  "globalAdminActiveStatus",
                                  item.status
                                );
                                switchToStatus(item.status); // Use cached data instead of API call
                              } else {
                                // 3rd Party Apps tab (only when idpLogin is true)
                                setActiveThirdPartyStatus(item.status);
                                processThirdPartyData();
                              }
                            }}
                            sx={{
                              borderRadius: "4px",
                              bgcolor:
                                ((!idpLogin || selectedTab === 0) &&
                                  activeStatus === item.status) ||
                                (idpLogin &&
                                  selectedTab === 1 &&
                                  activeThirdPartyStatus === item.status)
                                  ? "#E5F3FF"
                                  : "#ffffff",
                              padding: "2px",
                              paddingLeft: "40px",
                              cursor: "pointer",
                            }}
                          >
                            <ListItemText
                              primary={
                                <Typography
                                  fontSize="11px"
                                  lineHeight="20px"
                                  fontWeight={
                                    ((!idpLogin || selectedTab === 0) &&
                                      activeStatus === item.status) ||
                                    (idpLogin &&
                                      selectedTab === 1 &&
                                      activeThirdPartyStatus === item.status)
                                      ? "600"
                                      : "normal"
                                  }
                                  color={
                                    ((!idpLogin || selectedTab === 0) &&
                                      activeStatus === item.status) ||
                                    (idpLogin &&
                                      selectedTab === 1 &&
                                      activeThirdPartyStatus === item.status)
                                      ? "#0476E2"
                                      : "#2A2C2D"
                                  }
                                  display="grid"
                                  gridTemplateColumns="1fr auto"
                                  alignItems="center"
                                >
                                  <span
                                    style={{
                                      maxWidth: "200px",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "6px",
                                    }}
                                  >
                                    {React.cloneElement(item.icon, {
                                      style: {
                                        ...item.icon.props.style,
                                      },
                                      children: React.Children.map(
                                        item.icon.props.children,
                                        (child) =>
                                          React.cloneElement(child, {
                                            fill:
                                              ((!idpLogin ||
                                                selectedTab === 0) &&
                                                activeStatus === item.status) ||
                                              (idpLogin &&
                                                selectedTab === 1 &&
                                                activeThirdPartyStatus ===
                                                  item.status)
                                                ? "#0476E2"
                                                : "#12141599",
                                          })
                                      ),
                                    })}
                                    {item.text}
                                  </span>
                                  <span
                                    style={{
                                      display: "flex",
                                      minWidth: "30px",
                                      justifyContent: "flex-end",
                                      textAlign: "right",
                                      paddingRight: "13px",
                                    }}
                                  >
                                    {item.count}
                                  </span>
                                </Typography>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  </Box>
                )
              )}
            </Box>
            <Box
              sx={{
                flexGrow: 1,
                ml: 2,
                marginTop: "16px",
                paddingRight: "50px",
              }}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
                width="98%"
              >
                <Typography sx={{ fontSize: "20px" }}>
                  Applications -{" "}
                  <span
                    style={{
                      fontSize: "14px",
                      textTransform: "none",
                      color: "#5A5B5C",
                    }}
                  >
                    {formatStatusForDisplay(
                      selectedTab === 0 ? activeStatus : activeThirdPartyStatus
                    )}
                  </span>
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <TextField
                    variant="outlined"
                    size="small"
                    placeholder="Search for application name"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ width: 351 }}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                </Box>
              </Box>

              <Box sx={{ width: "98%" }}>
                <DataGrid
                  rows={getFilteredApplications()}
                  columns={getCurrentColumns()}
                  getRowId={(row) => row.id}
                  getRowClassName={(params) => {
                    if (params.row.isGroup) {
                      return "group-row";
                    }
                    if (params.row.isChild) {
                      return "child-row";
                    }
                    return "";
                  }}
                  pagination
                  pageSizeOptions={[5, 10, 15]}
                  paginationMode="client"
                  disableColumnMenu
                  checkboxSelection={false}
                  disableRowSelectionOnClick={true}
                  onRowClick={(row) =>
                    activeStatus === "DELETED" ? "" : clickApplications(row)
                  }
                  loading={
                    selectedTab === 0 && activeStatus === "DELETED"
                      ? deletedApplicationsLoading
                      : loader
                  }
                  components={{ NoRowsOverlay }}
                  componentsProps={{
                    noRowsOverlay: { showNoAppImage },
                  }}
                  paginationModel={{ page, pageSize }}
                  onPaginationModelChange={(model) => {
                    setPage(model.page);
                    setPageSize(model.pageSize);
                  }}
                  rowCount={getParentRowCount(getFilteredApplications())}
                  localeText={{
                    MuiTablePagination: {
                      labelRowsPerPage: "Items per page:",
                      labelDisplayedRows: ({ from, to, count }) =>
                        `${from}-${to} of ${
                          count === -1 ? `more than ${to}` : count
                        }`,
                      getItemAriaLabel: (type) => {
                        if (type === "first") {
                          return "Go to first page";
                        }
                        if (type === "last") {
                          return "Go to last page";
                        }
                        if (type === "next") {
                          return "Go to next page";
                        }
                        return "Go to previous page";
                      },
                    },
                    toolbarDensity: "Density",
                    toolbarDensityLabel: "Density",
                    toolbarDensityCompact: "Compact",
                    toolbarDensityStandard: "Standard",
                    toolbarDensityComfortable: "Comfortable",
                  }}
                  sx={{
                    height: "auto",
                    "& .MuiDataGrid-columnHeader": {
                      fontSize: "12px",
                      fontWeight: "600",
                      backgroundColor: "#f0f0f0 !important",
                      color: "#121415",
                      lineHeight: "30px",
                      padding: "0px 10px",
                      display: "flex",
                      alignItems: "center",
                      height: "40px",
                      minHeight: "40px",
                      maxHeight: "40px",
                    },
                    "& .MuiDataGrid-columnHeaderTitle": {
                      fontSize: "12px",
                      fontWeight: "bold",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-start",
                      minHeight: "30px",
                      lineHeight: "30px",
                      padding: "16px",
                    },
                    "& .MuiDataGrid-cell": {
                      borderRight: "1px solid rgba(224, 224, 224, 1)",
                      fontSize: "12px",
                      lineHeight: "20px",
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                      outline: "none",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },

                    "& .MuiDataGrid-cell:last-child": { borderRight: "none" },
                    "& .MuiDataGrid-container--top [role=row]": {
                      background: "#f0f0f0 !important",
                    },
                    "& .MuiDataGrid-virtualScroller": {
                      marginTop: "0px !important",
                    },
                    "& .MuiDataGrid-virtualScrollerContent": {
                      overflowX: "hidden !important",
                    },
                    "& .MuiDataGrid-columnHeader .MuiDataGrid-sortIcon": {
                      opacity: 1,
                      visibility: "visible",
                    },

                    "& .child-row": {
                      backgroundColor: "#ffffff !important",
                      "& .MuiDataGrid-cell:first-of-type": {
                        paddingLeft: "48px", // Indent only the first column
                      },
                    },
                    "& .MuiDataGrid-footerContainer": {
                      borderTop: "1px solid rgba(224, 224, 224, 1)",
                      backgroundColor: "#fff",
                      maxHeight: "52px",
                    },
                    "& .MuiTablePagination-root": {
                      fontSize: "14px",
                      color: "#495057",
                    },
                    "& .MuiTablePagination-selectLabel": {
                      fontSize: "14px",
                      fontWeight: "500",
                    },
                    "& .MuiTablePagination-displayedRows": {
                      fontSize: "14px",
                      fontWeight: "500",
                    },
                    "& .MuiTablePagination-select": {
                      fontSize: "14px",
                    },
                    "& .MuiIconButton-root": {
                      color: "#495057",
                      "&:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.04)",
                      },
                    },
                  }}
                />
              </Box>
            </Box>
          </Box>
          <div>&nbsp;</div>

          {/* DrawerFilter Component */}
          <DrawerFilter
            openFilter={openFilter}
            toggleDrawer={toggleDrawer}
            selectedCountries={memoizedCountries}
            selectedEcus={memoizedEcu}
            handleFilterApiCalls={handleFilterApiCalls}
          />

          {/* Error Snackbar */}
          <Snackbar
            open={openErrorPopup}
            autoHideDuration={6000}
            onClose={() => setOpenErrorPopup(false)}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <Alert
              onClose={() => setOpenErrorPopup(false)}
              severity="error"
              sx={{ width: "100%" }}
            >
              {errorMessage}
            </Alert>
          </Snackbar>

          {/* Success Snackbar */}
          <Snackbar
            open={openSuccessPopup}
            autoHideDuration={6000}
            onClose={() => setOpenSuccessPopup(false)}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <Alert
              onClose={() => setOpenSuccessPopup(false)}
              severity="success"
              sx={{ width: "100%" }}
            >
              {successMessage}
            </Alert>
          </Snackbar>

          {/* ECU Types Popup */}
          <Popover
            open={ecuTypesPopup.open}
            anchorEl={ecuTypesPopup.anchorEl}
            onClose={handleEcuTypesClose}
            anchorOrigin={{
              vertical: "center",
              horizontal: "center",
            }}
            transformOrigin={{
              vertical: "center",
              horizontal: "center",
            }}
            slotProps={{
              paper: {
                sx: {
                  padding: "16px",
                  minWidth: "250px",
                  maxWidth: "400px",
                  maxHeight: "300px",
                  borderRadius: "8px",
                  boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.15)",
                },
              },
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontSize: "16px",
                fontWeight: "bold",
                mb: 2,
                textAlign: "center",
              }}
            >
              ECU Types
            </Typography>
            <Box
              sx={{
                maxHeight: "200px",
                overflowY: "auto",
                overflowX: "hidden",
              }}
            >
              {ecuTypesPopup.data.map((ecuType, index) => (
                <Typography
                  key={ecuType}
                  sx={{
                    fontSize: "14px",
                    mb: 1,
                    padding: "6px 8px",
                    borderBottom:
                      index < ecuTypesPopup.data.length - 1
                        ? "1px solid #f0f0f0"
                        : "none",
                    wordWrap: "break-word",
                  }}
                >
                  {ecuType}
                </Typography>
              ))}
            </Box>
          </Popover>

          {/* Countries Popup */}
          <Popover
            open={countriesPopup.open}
            anchorEl={countriesPopup.anchorEl}
            onClose={handleCountriesClose}
            anchorOrigin={{
              vertical: "center",
              horizontal: "center",
            }}
            transformOrigin={{
              vertical: "center",
              horizontal: "center",
            }}
            slotProps={{
              paper: {
                sx: {
                  padding: "16px",
                  minWidth: "250px",
                  maxWidth: "400px",
                  maxHeight: "300px",
                  borderRadius: "8px",
                  boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.15)",
                },
              },
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontSize: "16px",
                fontWeight: "bold",
                mb: 2,
                textAlign: "center",
              }}
            >
              Countries
            </Typography>
            <Box
              sx={{
                maxHeight: "200px",
                overflowY: "auto",
                overflowX: "hidden",
              }}
            >
              {countriesPopup.data.map((country, index) => (
                <Typography
                  key={country}
                  sx={{
                    fontSize: "14px",
                    mb: 1,
                    padding: "6px 8px",
                    borderBottom:
                      index < countriesPopup.data.length - 1
                        ? "1px solid #f0f0f0"
                        : "none",
                    wordWrap: "break-word",
                  }}
                >
                  {country}
                </Typography>
              ))}
            </Box>
          </Popover>

          {/* Delete Confirmation Dialog */}
          <Dialog
            open={deleteConfirmDialog.open}
            onClose={handleDeleteCancel}
            aria-labelledby="delete-dialog-title"
            aria-describedby="delete-dialog-description"
          >
            <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
            <DialogContent>
              <DialogContentText id="delete-dialog-description">
                Are you sure you want to permanently delete the application "
                {deleteConfirmDialog.applicationName}"? This action cannot be
                undone.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={handleDeleteCancel}
                color="primary"
                variant="outlined"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteConfirm}
                color="error"
                variant="contained"
                autoFocus
              >
                Delete
              </Button>
            </DialogActions>
          </Dialog>

          {/* Reject Confirmation Dialog */}
          <Dialog
            open={rejectConfirmDialog.open}
            onClose={handleRejectCancel}
            aria-labelledby="reject-dialog-title"
            aria-describedby="reject-dialog-description"
          >
            <DialogTitle id="reject-dialog-title">Confirm Reject</DialogTitle>
            <DialogContent>
              <DialogContentText id="reject-dialog-description">
                Are you sure you want to reject the delete request for
                application "{rejectConfirmDialog.applicationName}"? This will
                cancel the delete request.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={handleRejectCancel}
                color="primary"
                variant="outlined"
                disabled={rejectLoading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleRejectConfirm}
                color="warning"
                variant="contained"
                autoFocus
                disabled={rejectLoading}
              >
                {rejectLoading ? "Rejecting..." : "Reject"}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </>
  );
}
